import { Button, Col, Form, message, Modal, Row, Tabs } from "antd";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import useIsMobile from "../../../../hooks/useIsMobile";
import {
  nutritionistInspectionAPI,
  updateNutritionistInspectionAPI,
} from "../../../../services/nutritionist.services";
import { NutritionistInitialContext } from "../../InspectionForms/HistoryForm/NutritionistInitialAssessmentProvider";
import ClinicalImpressionForm from "../../InspectionForms/ClinicalImpressionForm/ClinicalImpressionForm";
import NutritionistHistoryForm from "../../InspectionForms/HistoryForm/NutritionistHistoryForm";
import NutritionistGoalsForm from "../../InspectionForms/Goals/NutritionistGoalsForm";
import NutritionistDietAdvice from "../../InspectionForms/DietAdvice/NutritionistDietAdvice";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import NutritionistClinicalEvaluationForm from "../../InspectionForms/ClinicalEvaluation/NutritionistClinicalEvaluationForm";
import "../../ipforms.css";
import "../../Doctor/InitialInspection/InitialInspectionHeader.css";

const NutritionistInitialInspectionForm = ({
  setVisible,
  refreshPage,
  initialRefresh,
  editId,
  patientData,
  setConfirmModal,
  activeTab,
  setActiveTab,
}) => {
  const isMobile = useIsMobile();
  const formBodyRef = useRef(null);
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location?.search));
  const [clinicalForm] = Form.useForm();
  const [historyForm] = Form.useForm();
  const [clinicalEvaluationForm] = Form.useForm();
  const [goalsForm] = Form.useForm();
  const [dietForm] = Form.useForm();
  const [buttonPressed, setButtonPressed] = useState(false);
  const { formData } = useContext(NutritionistInitialContext);
  const [isDirty, setIsDirty] = useState(false);

  const tabItems = useMemo(
    () => [
      {
        key: "1",
        label: isMobile ? "Diagnosis" : "Clinical Impression/ Diagnosis",
      },
      { key: "2", label: "History" },
      {
        key: "3",
        label: isMobile ? "Evaluation" : "Clinical Evaluation",
      },
      { key: "4", label: "Goals" },
      { key: "5", label: isMobile ? "Diet" : "Diet Advice" },
    ],
    [isMobile]
  );

  useEffect(() => {
    if (formBodyRef.current) {
      formBodyRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  const handleSubmit = async (values, flag) => {
    try {
      if (editId) {
        await updateNutritionistInspectionAPI(editId, {
          ...values,
          diet_type:
            values?.diet_advice?.diet_details?.diet ??
            formData?.diet_advice?.diet_details?.diet,
        });
        initialRefresh();
      } else {
        await nutritionistInspectionAPI({
          ...values,
          diet_type:
            values?.diet_advice?.diet_details?.diet ??
            formData?.diet_advice?.diet_details?.diet,
        });
        refreshPage();
        initialRefresh();
      }

      message.success(`${flag} submitted successfully`);
      if (buttonPressed) {
        if (activeTab === "5") {
          setActiveTab("1");
          setVisible(false);
        } else {
          const nextTab = (parseInt(activeTab) + 1).toString();
          setActiveTab(nextTab);
        }
        setButtonPressed(false);
      }
      setIsDirty(false);
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const onClinicalSubmit = (values) => {
    handleSubmit(
      { clinical_diagnosis: values, admission_id: params?.patient },
      "Clinical Impression/Diagnosis"
    );
  };

  const onHistorySubmit = (values) => {
    handleSubmit({ history: values, admission_id: params?.patient }, "History");
  };
  const onClinicalEvaluationSubmit = (values) => {
    handleSubmit(
      {
        clinical_evaluation: values,
        admission_id: params?.patient,
      },
      "Clinical Evaluation"
    );
  };

  const onGoalsSubmit = (values) => {
    handleSubmit(
      {
        goals: values,
        admission_id: params?.patient,
      },
      "Goals"
    );
  };
  const onDietSubmit = (values) => {
    handleSubmit(
      {
        diet_advice: values,
        admission_id: params?.patient,
        // intial_assessment_done: true,
      },
      "Diet Advice"
    );
  };

  const getFormKey = () => {
    switch (activeTab) {
      case "1":
        return clinicalForm.submit();
      case "2":
        return historyForm.submit();
      case "3":
        return clinicalEvaluationForm.submit();
      case "4":
        return goalsForm.submit();
      case "5":
        return dietForm.submit();
      default:
        return "";
    }
  };

  const handleTabChange = (key) => {
    if (isDirty) {
      Modal.confirm({
        title: "Unsaved changes",
        content:
          "You have unsaved changes. Are you sure you want to save these changes?",
        onOk: () => {
          switch (activeTab) {
            case "1":
              clinicalForm.validateFields().then((values) => {
                setIsDirty(false);
                onClinicalSubmit(values);
                setActiveTab(key);
              });
            case "2":
              historyForm.validateFields().then((values) => {
                setIsDirty(false);
                onHistorySubmit(values);
                setActiveTab(key);
              });
              break;
            case "3":
              clinicalEvaluationForm.validateFields().then((values) => {
                setIsDirty(false);
                onClinicalEvaluationSubmit(values);
                setActiveTab(key);
              });
              break;
            case "4":
              goalsForm.validateFields().then((values) => {
                setIsDirty(false);
                onGoalsSubmit(values);
                setActiveTab(key);
              });
              break;
            case "5":
              dietForm.validateFields().then((values) => {
                setIsDirty(false);
                onDietSubmit(values);
                setActiveTab(key);
              });
            default:
              setActiveTab(key);
          }
        },
        onCancel: () => {
          setActiveTab(key);
          setIsDirty(false);
        },
      });
    } else {
      setActiveTab(key);
    }
  };

  const onFormValuesChange = () => {
    setIsDirty(true);
  };

  return (
    <div className="initial-inspection-form-wrapper">
      <div className="ia-form-tabs">
        <Row>
          <Col
            xs={24}
            sm={24}
            md={{ span: 23, offset: 1 }}
            lg={{ span: 21, offset: 3 }}
            xl={{ span: 19, offset: 5 }}
            xxl={{ span: 18, offset: 6 }}
          >
            <Tabs
              activeKey={activeTab}
              items={tabItems}
              onChange={handleTabChange}
            />
          </Col>
        </Row>
      </div>
      <div className="ia-form-panel">
        <div className="ia-form-body" ref={formBodyRef}>
          {activeTab === "1" && (
            <Form
              id="clinical-diagnosis-form"
              form={clinicalForm}
              layout="vertical"
              onFinish={onClinicalSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <ClinicalImpressionForm
                form={clinicalForm}
                formData={formData}
                context={NutritionistInitialContext}
                editId={editId}
                patientData={patientData}
              />
            </Form>
          )}
          {activeTab === "2" && (
            <Form
              id="history-form"
              form={historyForm}
              layout="vertical"
              onFinish={onHistorySubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <NutritionistHistoryForm
                form={historyForm}
                formData={formData}
                editId={editId}
                patientData={patientData}
              />
            </Form>
          )}
          {activeTab === "3" && (
            <Form
              id="clinical-evaluation-form"
              form={clinicalEvaluationForm}
              layout="vertical"
              onFinish={onClinicalEvaluationSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <NutritionistClinicalEvaluationForm
                form={clinicalEvaluationForm}
                formData={formData}
                patientData={patientData}
              />
            </Form>
          )}
          {activeTab === "4" && (
            <Form
              id="goals-form"
              form={goalsForm}
              layout="vertical"
              onFinish={onGoalsSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData?.goals}
              className="ip-forms"
            >
              <NutritionistGoalsForm form={goalsForm} formData={formData} />
            </Form>
          )}
          {activeTab === "5" && (
            <Form
              id="diet-advice-form"
              form={dietForm}
              layout="vertical"
              onFinish={onDietSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <NutritionistDietAdvice form={dietForm} formData={formData} />
            </Form>
          )}
        </div>
        <div className="ia-form-footer">
          <Button
            onClick={() => {
              getFormKey();
              setButtonPressed(true);
              document
                .querySelector(".initial-inspection-form-wrapper")
                ?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
            }}
            type="primary"
            className={btnStyles.mdBtn}
            style={{ fontWeight: 500 }}
          >
            {activeTab === "5" ? "Done" : " Save & Proceed"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NutritionistInitialInspectionForm;
